/**
 * The "new version available" prompt — the consumer side of the ADR-0008 §5
 * update handshake.
 *
 * ⚠️ WITHOUT THIS COMPONENT THE HANDSHAKE IS INERT, AND THAT IS NOT A COSMETIC
 *    GAP. `sw.js` deliberately never calls `skipWaiting()` on install: a new
 *    worker installs and then WAITS until every tab of the app is closed. On an
 *    installed PWA — especially iOS, where the app sits in the app switcher for
 *    days — that effectively never happens.
 *
 *    The consequence is silent and severe: the OLD worker keeps controlling the
 *    page, so a customer who has enabled notifications receives pushes that the
 *    old worker has no handler for. The push service returns 2xx, the sender
 *    records `sent`, and NOTHING appears on the device. Every layer reports
 *    success while the feature is dead. That exact failure was diagnosed on
 *    2026-07-30 and took the whole delivery chain to rule out — the fix was
 *    "close the app completely", which no customer will ever think to do.
 *
 *    So this prompt is what keeps push working across deploys. Treat it as part
 *    of the notification feature, not as chrome.
 *
 * Deliberately NOT auto-applying: `applyUpdate()` reloads the page. Doing that
 * without asking would discard whatever the customer was in the middle of.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { applyUpdate, isUpdateAvailable, subscribeUpdateAvailable } from '../push/registerServiceWorker.ts';
import styles from './UpdatePrompt.module.css';

export function UpdatePrompt(): React.JSX.Element | null {
  const { t } = useTranslation();

  // Seeded from the point-in-time snapshot, not just the subscription: the
  // worker can finish installing before this component mounts, and that update
  // would otherwise never be announced.
  const [available, setAvailable] = useState<boolean>(() => isUpdateAvailable());
  const [applying, setApplying] = useState(false);

  useEffect(() => subscribeUpdateAvailable(() => setAvailable(true)), []);

  if (!available) return null;

  const onApply = (): void => {
    // `applyUpdate()` triggers a reload once the new worker takes control, so
    // there is no "finished" state to render — only the in-flight one, which
    // exists to stop a second tap re-posting SKIP_WAITING.
    setApplying(true);
    applyUpdate();
  };

  return (
    <div className={styles['bar']} role="status" aria-live="polite" data-testid="update-prompt">
      <span className={styles['text']}>{t('update.available')}</span>
      <button
        type="button"
        className={styles['action']}
        onClick={onApply}
        disabled={applying}
        data-testid="update-prompt-apply"
      >
        {applying ? t('update.applying') : t('update.action')}
      </button>
    </div>
  );
}
